import { useLocation } from 'react-router-dom'
import RouteMap from '../../../core/router/route.map'
import UserProfileService from '../../../shared/services/UserProfile.service'

/**
 * Returns true when the given pathname maps to a backoffice route in the
 * central RouteMap (`guards.requiresAuth: true`). Mirrors RouteMatcher's
 * matching rule (`r.path === pathname` OR `r.pattern.test(pathname)`).
 * Unknown paths return false — err on the side of hiding the widget.
 *
 * `/` and empty paths explicitly return false so the home route never matches
 * unpredictably against permissive RouteMap entries.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
function routeRequiresAuth(pathname) {
  if (!pathname || pathname.trim() === '/') return false
  for (const r of RouteMap.routes) {
    const matchesExact = r.path && r.path.length > 1 && r.path === pathname
    const matchesPattern = r.pattern && r.pattern.test(pathname)
    if (matchesExact || matchesPattern) return !!r.guards?.requiresAuth
  }
  return false
}

/**
 * Three-tier visibility gate for slide-out sidebars, plus auth + role checks.
 *
 * The gate composes four short-circuit rules in order. The first one that
 * matches decides visibility; downstream rules are not consulted. Returning
 * `false` causes the caller to render nothing — so service initialization
 * (Echo, REST hydration) inside the sidebar never runs on excluded surfaces.
 *
 *   1. Auth — `UserProfileService.getUserId()` must be > 0. Login does a
 *      hard navigation, so a synchronous check at mount is sufficient.
 *   2. Role — `UserProfileService.isA(blockedRoles)` short-circuits when the
 *      current user matches any blocked role name (e.g. 'affiliate-group').
 *      `isA` accepts a string or string array; values are mapped to usertype
 *      ids internally via PermissionService.
 *   3. `disabledRoutes` — explicit deny (highest route priority, cannot be
 *      overridden). Prefix-matched. Used for chromeless surfaces (e.g. the
 *      GHL Custom Page iframe at /10x-sales-tracker) and routes that
 *      technically require auth but where the widget is UX-inappropriate
 *      (login, password reset, onboarding wizards).
 *   4. `enabledRoutes` — explicit allow that overrides the default rule
 *      in (5). Prefix-matched.
 *   5. Default — render only when the matched RouteMap entry has
 *      `guards.requiresAuth: true`. Unknown paths and public-facing surfaces
 *      (home, marketing, agent subsites) return false and stay hidden.
 *
 * @param {Object} opts
 * @param {string[]} [opts.disabledRoutes=[]] - Route prefixes that hide the sidebar.
 * @param {string[]} [opts.enabledRoutes=[]] - Route prefixes that show the sidebar regardless of auth-gating default.
 * @param {string[]} [opts.blockedRoles=[]] - Role names (UserProfileService.isA contract) that hide the sidebar.
 * @returns {{ visible: boolean }}
 */
function useSidebarGate({ disabledRoutes = [], enabledRoutes = [], blockedRoles = [] } = {}) {
  const { pathname } = useLocation()

  const userId = UserProfileService.getUserId()
  if (!userId || userId <= 0) return { visible: false }
  if (blockedRoles.length > 0 && UserProfileService.isA(blockedRoles)) {
    return { visible: false }
  }
  if (disabledRoutes.some(r => pathname.startsWith(r))) {
    return { visible: false }
  }
  if (enabledRoutes.some(r => pathname.startsWith(r))) {
    return { visible: true }
  }
  return { visible: routeRequiresAuth(pathname) }
}

export default useSidebarGate
